import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { IonButton, IonInput, IonItem, IonToast, IonSpinner, IonIcon, IonCheckbox, IonLabel, IonSelectOption } from '@ionic/angular/standalone';
import { jwtDecode } from 'jwt-decode';
import { AuthService } from 'src/app/services/auth.service';
import { NavController } from '@ionic/angular';
import { MotoristaService } from 'src/app/services/motorista-service/motorista.service';
import { ResetPasswordService } from 'src/app/services/reset-password-service/reset-password-service.service';
import { isPlatform } from '@ionic/angular';
import { StorageService } from 'src/app/services/storage-service/storage.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [IonIcon, IonSpinner, IonItem, IonInput, IonButton, HttpClientModule,
    ReactiveFormsModule, IonToast, CommonModule, FormsModule, IonCheckbox,
    IonLabel, IonSelectOption
  ]
})
export class LoginComponent implements OnInit {

  //Injeções 
  authService = inject(AuthService);
  router = inject(Router);
  fb = inject(FormBuilder);
  fbRegister = inject(FormBuilder);
  fbPassword = inject(FormBuilder);
  http = inject(HttpClient);
  navCtrl = inject(NavController);
  motoristaService = inject(MotoristaService);
  resetPasswordService = inject(ResetPasswordService);
  route = inject(ActivatedRoute);
  storageService = inject(StorageService);

  //Formularios
  formGroup: FormGroup;
  primeiroAcessoForm: FormGroup;
  alteraSenhaForm: FormGroup

  isApp: boolean = isPlatform('hybrid'); // Identifica se é app nativo
  keepLoggedIn: boolean = true; // Checkbox "Manter conectado" (padrão true)
  isLoading: boolean = false; // Para controlar loading do botão
  loginAutomaticoExecutado: boolean = false;

  //Mensagens
  showToast = false;
  toastMessage = '';

  errorMessage: string = '';
  mensagem: string = '';
  isChangingPassword: boolean = false;
  isFirstAccess: boolean = false;
  showPassword: boolean = false;
  identificador = '';
  senha = '';
  showRecoverModal = false;
  isLoadingRecover = false;
  empresas: any[] = [];
  tenant_id = '';

  constructor() {
    this.formGroup = this.fb.group({
      identificador: ['', [Validators.required]],
      senha: ['', [Validators.required, Validators.minLength(6)]]
    });

    this.primeiroAcessoForm = this.fbRegister.group({
      identificador: ['', [Validators.required]]
    })

    this.alteraSenhaForm = this.fbPassword.group({
      senha: ['', [Validators.required, Validators.minLength(6)]],
      confirmaSenha: ['', [Validators.required, Validators.minLength(6)]]
    })
  }

  async ngOnInit(): Promise<void> {

    const servidor = await this.storageService.getServidor();

    if (!servidor || !servidor.url_servidor) {
      this.router.navigate(['/url-servico']);
      return;
    }

    const online = this.authService.isOnline();
    const loginValidoHoje = await this.authService.isLoginValidoHoje();

    const retaguarda = this.route.snapshot.queryParamMap.get('retaguarda') === 'true';

    /*if (isPlatform('hybrid')) {
      console.log('Rodando como APP, permanece no login');
    } else {
      if (!retaguarda) {
        this.router.navigate(['/first-page']);
        return;
      }
    }*/

    // 🔥 MUDANÇA: Só tenta login automático se estiver ONLINE
    if (this.isApp && !this.loginAutomaticoExecutado) {
      if (online) {
        console.log('✅ App online, tentando login automático...');
        const fezLogin = await this.tentarLoginAutomatico();
        if (fezLogin) {
          console.log('✅ Login automático bem sucedido');
          return;
        }
      } else {
        console.log('⚠️ App está offline, pulando login automático');
      }
    }

    // 🔥 MUDANÇA: Verifica login offline (funcionalidade antiga)
    if (!online && loginValidoHoje) {
      console.log('✅ Offline mas login válido hoje, redirecionando...');
      const role = await this.authService.getRole();

      if (role === 'ROLE_GERENTE') {
        this.router.navigate(['gerente']);
      } else if (role === 'ROLE_MOTORISTA') {
        this.router.navigate(['motorista']);
      }
      return;
    }

    // 🔥 MUDANÇA: Só faz logout se NÃO tiver credenciais salvas
    const hasCreds = await this.authService.hasCredentials();
    if (!hasCreds) {
      await this.authService.logout();
    }
  }

  // 🔥 MUDANÇA: Agora retorna boolean
  async tentarLoginAutomatico(): Promise<boolean> {
    try {
      this.loginAutomaticoExecutado = true;
      console.log('🔍 Verificando credenciais salvas...');

      const credenciais = await this.authService.getCredentials();

      if (credenciais) {
        console.log('✅ Credenciais encontradas, tentando login automático...');
        console.log('📱 Usuário:', credenciais.identificador);

        this.formGroup.patchValue({
          identificador: credenciais.identificador,
          senha: credenciais.senha
        });

        await this.onLogin();
        return true; // 🔥 MUDANÇA: retorna true se logou
      } else {
        console.log('ℹ️ Nenhuma credencial salva encontrada');
        return false; // 🔥 MUDANÇA: retorna false se não logou
      }
    } catch (error) {
      console.error('❌ Erro no login automático:', error);
      // 🔥 MUDANÇA: Só limpa credenciais se estiver online
      if (this.isApp && this.authService.isOnline()) {
        await this.authService.clearCredentials();
      }
      return false;
    }
  }

  async ionViewWillEnter() {
    console.log('📱 ionViewWillEnter - isApp:', this.isApp);
    // 🔥 MUDANÇA: Só reseta se não for app
    if (!this.isApp) {
      await this.authService.logout();
      this.isChangingPassword = false;
      this.formGroup.reset();
    }
  }

  onEmpresaChange(event: any) {
    this.authService.storeTenant(event.detail.value, this.empresas.find(f => f.tenant_id == event.detail.value).versao);
  }

  async onLogin() {
    if (!this.formGroup.valid) {
      this.toastMessage = 'Por favor, preencha o formulário corretamente.';
      this.showToast = true;
      return;
    }

    const { identificador, senha } = this.formGroup.value;

    try {

      await this.authService.initStorage();

      const token: string = await this.authService.login(identificador, senha);

      if (this.isApp) {
        if (this.keepLoggedIn) {
          await this.authService.saveCredentials(identificador, senha);
          console.log('✅ Credenciais salvas para próximo acesso');
        } else {
          await this.authService.clearCredentials();
          console.log('🗑️ Credenciais removidas (não manter conectado)');
        }
      }

      const decoded: any = jwtDecode(token);
      if (decoded.trocarSenha) {
        this.router.navigate(['/auth/change-password'], {
          queryParams: { identificador, origem: 'login' }
        });
        return;
      }

      const authorities = decoded.authorities || [];
      const role = authorities.length > 0 ? authorities[0] : '';

      this.empresas = decoded.tenant_id;

      const tenant_id = await this.authService.getTenantId();
      if (!tenant_id) {
        return;
      }

      if (decoded.trocarSenha) {
        this.isChangingPassword = true;
        return;
      }

      this.proximaPagina(role);

    } catch (error: any) {
      console.error('Erro no login:', error);

      if (this.isApp && this.authService.isOnline()) {
        await this.authService.clearCredentials();
      }

      if (error.status === 0) {
        this.toastMessage = 'Falha de conexão com o servidor. Verifique sua internet ou a URL configurada.';
      } else if (error.status === 403) {
        this.toastMessage =
          (typeof error.error === 'string' && error.error.trim()) ||
          'Este usuário está desativado. Entre em contato com o administrador para reativar o acesso.';
      } else if (error.status === 401) {
        this.toastMessage = 'Credenciais inválidas. Verifique o identificador e a senha.';
      } else {
        this.toastMessage = error.error?.message || 'Ocorreu um erro inesperado.';
      }

      this.showToast = true;
    }
  }

  private proximaPagina(role: any) {
    if (role === 'ROLE_GERENTE') {
      this.navCtrl.navigateRoot('/gerente');
    } else if (role === 'ROLE_MOTORISTA') {
      this.navCtrl.navigateRoot('/motorista');
    } else {
      this.navCtrl.navigateRoot('/url-servico');
    }
  }

  onFirstAccess() {
    this.isFirstAccess = true;
    this.isChangingPassword = false;
  }

  enviarEmail() {
    if (this.primeiroAcessoForm.valid) {
      const identificador = this.primeiroAcessoForm.get('identificador')?.value;
      this.authService.enviarEmailPrimeiroAcesso(identificador).subscribe({
        next: (response: any) => {
          this.mensagem = response;
          this.cancelar();
        },
        error: (error: any) => {
          this.mensagem = 'Erro ao processar o pedido. Tente novamente.';
        },
      });
    }
  }

  cancelar() {
    this.isFirstAccess = false;
    this.isChangingPassword = false;
    this.formGroup.reset();
    this.primeiroAcessoForm.reset();
  }

  alterarSenha() {
    if (this.alteraSenhaForm.valid) {
      const identificador = this.formGroup.get('identificador')?.value;
      const senha = this.alteraSenhaForm.get('senha')?.value;
      const confirmaSenha = this.alteraSenhaForm.get('confirmaSenha')?.value;

      if (senha === confirmaSenha) {
        this.authService.alterarSenha(identificador, senha).subscribe({
          next: (response: any) => {
            this.mensagem = 'Senha alterada com sucesso!';
            this.onLogin();
            this.alteraSenhaForm.reset();
            //this.cancelar();
            this.router.navigate(['/auth/login']);
          },
          error: (error: any) => {
            this.mensagem = 'Erro ao alterar a senha. Tente novamente.';
          }
        });
      } else {
        this.mensagem = 'As senhas não correspondem. Por favor, tente novamente.';
      }
    } else {
      this.mensagem = 'Por favor, preencha o formulário corretamente.';
    }
  }

  // método chamado pelo (click)
  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  recoverForm = this.fb.group({
    matricula: ['', Validators.required]
  });

  openRecoverModal() {
    this.showRecoverModal = true;
  }

  closeRecoverModal() {
    this.showRecoverModal = false;
    this.recoverForm.reset();
  }

  /*confirmRecover() {
    if (this.recoverForm.invalid) {
      this.toastMessage = "Informe a matrícula.";
      this.showToast = true;
      return;
    }

    const matricula = this.recoverForm.get('matricula')?.value ?? '';

    this.motoristaService.buscarMotoristaPorIdentificador(matricula)
      .subscribe({
        next: (motorista: any) => {

          if (!motorista || !motorista.email || !motorista.id) {
            this.toastMessage = "Usuário encontrado, mas sem e-mail cadastrado.";
            this.showToast = true;
            return;
          }

          // Agora chama o envio do email
          this.resetPasswordService
            .enviarEmailRecuperacao(motorista.id, motorista.email)
            .subscribe({
              next: (msg) => {
                this.toastMessage = msg;
                this.showToast = true;
                this.closeRecoverModal();
              },
              error: () => {
                this.toastMessage = "Erro ao enviar e-mail.";
                this.showToast = true;
              }
            });

        },
        error: (err) => {
          console.error("Erro ao buscar motorista:", err);
          this.toastMessage = err.status === 404
            ? "Matrícula não encontrada."
            : "Erro ao buscar matrícula.";

          this.showToast = true;
        }
      });
  }*/

  async confirmRecover() {
    if (this.recoverForm.invalid) {
      this.toastMessage = "Informe a matrícula.";
      this.showToast = true;
      return;
    }

    this.isLoadingRecover = true;

    const matricula = this.recoverForm.get('matricula')?.value ?? '';

    this.motoristaService.buscarMotoristaPorIdentificador(matricula)
      .subscribe({
        next: (motorista: any) => {
          if (!motorista || !motorista.email || !motorista.id) {
            this.toastMessage = "Usuário encontrado, mas sem e-mail cadastrado.";
            this.showToast = true;
            this.isLoadingRecover = false;
            return;
          }

          this.resetPasswordService
            .enviarEmailRecuperacao(motorista.id, motorista.email)
            .subscribe({
              next: (msg) => {
                this.toastMessage = msg;
                this.showToast = true;
                this.closeRecoverModal();
                this.isLoadingRecover = false;
              },
              error: () => {
                this.toastMessage = "Erro ao enviar e-mail.";
                this.showToast = true;
                this.isLoadingRecover = false;
              }
            });
        },
        error: (err) => {
          console.error("Erro ao buscar motorista:", err);
          this.toastMessage = err.status === 404
            ? "Matrícula não encontrada."
            : "Erro ao buscar matrícula.";
          this.showToast = true;
          this.isLoadingRecover = false;
        }
      });
  }

  async limparServidor() {
    try {
      const db = await this.storageService['dbPromise'];
      await (await db).clear('servidor');

      this.toastMessage = 'Servidor limpo com sucesso!';
      this.showToast = true;

      this.router.navigate(['/url-servico']);
    } catch (error) {
      console.error('Erro ao limpar servidor:', error);
      this.toastMessage = 'Erro ao limpar servidor!';
      this.showToast = true;
    }
  }

  abrirAjuda() {
    window.open('https://innovatransit.com.br/ajudaaplicativologin/', '_blank');
  }

  async fazerLogout() {
    if (this.isApp) {
      await this.authService.clearCredentials();
    }
    await this.authService.logout();
    this.router.navigate(['/auth/login']);
  }

  voltarParaCredenciais() {
    this.empresas = [];
    this.tenant_id = '';
  }
}

